/**
 * W5 LOC-3 · CROC_LOC_PIN_UI_v1 — map pin fidelity lattice (pure Soft invent invent coords).
 * Soft invent: online pulse ⇒ position green · Soft invent free-float pin · Soft invent GeoIP glow.
 */

export const LOC_PIN_LATTICE = "CROC_LOC_PIN_UI_v1";
export const LOC_PIN_FIDELITY_LATTICE = "CROC_LOC_PIN_FIDELITY_v1";

/** @typedef {"FRESH"|"STALE"|"HOLD"|"NONE"} LocPin */
/** @typedef {"show"|"dim"|"hold"|"hide"} LocRender */

/**
 * @param {Record<string, unknown>|null|undefined} row map-snapshot item
 * @returns {{ pin: LocPin, mapGreen: boolean, ageS: number|null, source: string, render: LocRender, cssClass: string, honesty: string }}
 */
export function resolveLocationPinUi(row) {
  const r = row && typeof row === "object" ? row : {};
  const lat = Number(r.map_lat);
  const lng = Number(r.map_lng);
  const hasLl = Number.isFinite(lat) && Number.isFinite(lng);
  const pinRaw = String(r.location_pin || "").trim().toUpperCase();
  let pin =
    pinRaw === "FRESH" || pinRaw === "STALE" || pinRaw === "HOLD" || pinRaw === "NONE"
      ? pinRaw
      : hasLl
        ? "STALE"
        : "NONE";
  const mapGreen = r.location_map_green === true || r.location_map_green === 1;
  const src = String(r.location_source || "").trim().toUpperCase();
  const ageRaw = r.location_age_s;
  const ageS =
    ageRaw == null || ageRaw === ""
      ? null
      : Number.isFinite(Number(ageRaw))
        ? Number(ageRaw)
        : null;

  if (!hasLl) {
    return {
      pin: "NONE",
      mapGreen: false,
      ageS,
      source: src,
      render: "hide",
      cssClass: "cc-marker--loc-none",
      honesty: "Soft!=free_float Soft!=no_coords",
    };
  }
  if (src === "GEOIP" || src === "IP_GUESS") {
    return {
      pin: "HOLD",
      mapGreen: false,
      ageS,
      source: src,
      render: "hold",
      cssClass: "cc-marker--loc-hold",
      honesty: "Soft!=GeoIP_green Soft!=IP_guess_pin",
    };
  }
  if (src === "L2_BROTHER") {
    return {
      pin: "STALE",
      mapGreen: false,
      ageS,
      source: src,
      render: "dim",
      cssClass: "cc-marker--loc-stale",
      honesty: "L2_BROTHER Soft!=metres Soft!=GeoIP Soft!=prefix_only",
    };
  }
  // Server map_green is authoritative for FRESH green ring Soft invent invent.
  if (pin === "FRESH" && !mapGreen) {
    pin = "STALE";
  }
  if (pin === "FRESH" && mapGreen) {
    return {
      pin: "FRESH",
      mapGreen: true,
      ageS,
      source: src || "LEGACY",
      render: "show",
      cssClass: "cc-marker--loc-fresh",
      honesty: "ADMIT_FRESH Soft!=online_eq_green",
    };
  }
  if (pin === "STALE") {
    return {
      pin: "STALE",
      mapGreen: false,
      ageS,
      source: src || "LEGACY_MAP",
      render: "dim",
      cssClass: "cc-marker--loc-stale",
      honesty: "STALE Soft!=fresh_green Soft!=free_float",
    };
  }
  if (pin === "HOLD") {
    return {
      pin: "HOLD",
      mapGreen: false,
      ageS,
      source: src,
      render: "hold",
      cssClass: "cc-marker--loc-hold",
      honesty: "HOLD Soft!=green",
    };
  }
  return {
    pin: "NONE",
    mapGreen: false,
    ageS,
    source: src,
    render: "hide",
    cssClass: "cc-marker--loc-none",
    honesty: "NONE Soft!=draw_without_admit",
  };
}

/**
 * Apply location lattice classes on marker DOM Soft invent invent coordinates.
 * @param {HTMLElement|null|undefined} el
 * @param {Record<string, unknown>|null|undefined} row
 */
export function applyLocationPinToMarkerEl(el, row) {
  if (!el || !el.classList) return null;
  const ui = resolveLocationPinUi(row);
  el.classList.remove(
    "cc-marker--loc-fresh",
    "cc-marker--loc-stale",
    "cc-marker--loc-hold",
    "cc-marker--loc-none",
  );
  el.classList.add(ui.cssClass);
  el.setAttribute("data-loc-pin", ui.pin);
  el.setAttribute("data-loc-green", ui.mapGreen ? "1" : "0");
  el.setAttribute("data-loc-source", ui.source || "");
  el.setAttribute("data-loc-lattice", LOC_PIN_LATTICE);
  el.setAttribute("data-loc-fidelity", LOC_PIN_FIDELITY_LATTICE);
  if (ui.ageS != null) el.setAttribute("data-loc-age-s", String(ui.ageS));
  else el.removeAttribute("data-loc-age-s");
  const baseTitle = String(el.title || "").replace(/\s*·\s*loc:[^·]*$/i, "").trim();
  const locTitle =
    ui.pin === "FRESH"
      ? "loc:fresh"
      : ui.pin === "STALE"
        ? "loc:stale Soft!=fresh"
        : ui.pin === "HOLD"
          ? "loc:hold Soft!=green"
          : "loc:none";
  el.setAttribute("aria-label", locTitle.replace(" Soft!=", " "));
  el.title = baseTitle ? `${baseTitle} · ${locTitle}` : locTitle;
  return ui;
}
